/**
 * @description Component-DayTable
 */
import './index.less';

import { useDebounceFn } from 'ahooks';
import classNames from 'classnames';
import _ from 'lodash';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import RefreshIcon from '@/assets/svg/icon_refresh_linear.svg?react';
import { Button } from '@/components/Button';
import CsvExportButton from '@/components/Button/CsvExportButton';
import SkeletonTable, { SkeletonTableColumnsType } from '@/components/SkeletonTable';
import type { TableSticky } from '@/components/Table';
import Table from '@/components/Table';
import { HISTORY_TYPE } from '@/constants/history';
import { WrappedPair } from '@/entities/WrappedPair';
import { useHistoryRange } from '@/features/graph/hooks';
import {
  AccountBalanceHistory,
  FundingHistory,
  LiquidityHistory,
  OrderHistory,
  TradeHistory,
  TransferHistory,
} from '@/features/graph/types';
import { useStickyObserver } from '@/hooks/useStickyPin';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';

import { DEFAULT_HISTORY_PAGE_SIZE_LOCAL } from '@/features/graph/constants';
import TradeHistoryTabs from '@/pages/trade/TradePortfolio/common';
import HistoryRefreshSimple from '../HistoryRefreshSimple';
import { useHistoryTableColumns } from '../tableConfigs';
import HistoryTableRangeSelector from './HistoryTableRangeSelector';

interface IPropTypes {
  pair?: WrappedPair;
  historyType: HISTORY_TYPE;
  histories:
    | TradeHistory[]
    | OrderHistory[]
    | FundingHistory[]
    | TransferHistory[]
    | LiquidityHistory[]
    | AccountBalanceHistory[];
  showPair?: boolean;
  loading: boolean;
  rowCount?: number;
  pageType?: 'portfolio' | 'trade';
  isShowSelector?: boolean;
  refresh?: () => void;
  showTradeHistoryTabs?: boolean;
  sticky?: boolean | TableSticky;
  isTradePage?: boolean;
  fetchMoreData?: () => void;
}
const HistoryTable: FC<IPropTypes> = function ({
  pair,
  historyType,
  histories,
  showPair = true,
  rowCount = 2,
  loading,
  pageType = 'trade',
  isShowSelector = true,
  refresh,
  sticky,
  fetchMoreData,
  isTradePage,
  showTradeHistoryTabs = false,
}) {
  const { t } = useTranslation();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const [messageClosed, setMessageClosed] = useState(false);
  const timeRange = useHistoryRange();
  const containerRef = useRef<HTMLDivElement>(null);
  const selectorRef = useRef<HTMLDivElement>(null);
  const [scrollY, setScrollY] = useState(251);
  // const [scrollX, setScrollX] = useState(600);
  const [page, setPage] = useState(1);

  const filteredHistories = useMemo(
    () => histories,

    [histories],
  );

  const localHistories = useMemo(
    () => filteredHistories.slice(0, page * DEFAULT_HISTORY_PAGE_SIZE_LOCAL),
    [filteredHistories, page],
  );

  const quotes = useMemo(() => {
    const quotes = _.uniqBy(
      filteredHistories.map((history) => (history as AccountBalanceHistory)?.tokenInfo).filter((h) => h !== undefined),
      'address',
    );
    return quotes;
  }, [filteredHistories]);

  useStickyObserver('.syn-history-table-header', 40);
  const columnDefs = useHistoryTableColumns(historyType, quotes, showPair);
  const setTableParams = useCallback(() => {
    if (containerRef.current) {
      const scrollY =
        containerRef.current.offsetHeight - (selectorRef.current ? selectorRef.current.offsetHeight + 49 : 40);
      console.log('🚀 ~ HistoryTable ~ scrollY:', scrollY);
      setScrollY(scrollY > 400 ? (isTradePage ? 400 : scrollY) : scrollY < 251 ? 251 : scrollY);
      // setScrollX(containerRef.current.offsetWidth - 16);
    }
  }, [setScrollY, isTradePage]);

  const { run: fetchMoreDataDebounce } = useDebounceFn(
    () => {
      fetchMoreData && fetchMoreData();
    },
    { wait: 100 },
  );

  useEffect(() => {
    let timeOut: NodeJS.Timeout;
    if (!loading) {
      timeOut = setTimeout(() => setTableParams(), 0);
      window.onresize = setTableParams;
    }
    return () => timeOut && clearTimeout(timeOut);
    //  must depend on historyType
  }, [loading, historyType]);
  const { run: onScroll } = useDebounceFn(
    (e: Event) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target as HTMLTableSectionElement;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        fetchMoreDataDebounce();
        setPage((pre) => pre + 1);
      }
    },
    { wait: 20 },
  );
  useEffect(() => {
    if (!loading && !!filteredHistories.length) {
      const el = document.querySelector('#historyTable .ant-table-tbody-virtual-holder') as HTMLTableSectionElement;
      if (el) {
        el.addEventListener('scroll', onScroll);
      }
    }
  }, [loading, fetchMoreDataDebounce]);

  useEffect(() => {
    setPage(1);
  }, [chainId, historyType, timeRange, userAddr]);
  const emptyDescProps = useMemo(() => {
    if (pageType === 'portfolio' && historyType === HISTORY_TYPE.LIQUIDITY) {
      return {
        emptyDesc: t('common.empty.EarnNow'),
        emptyDescLink: '/#/earn',
      };
    }
    return {
      emptyDesc: pageType === 'portfolio' ? t('common.empty.TradeNow') : undefined,
      emptyDescLink: pageType === 'portfolio' ? '/#/trade' : undefined,
    };
  }, [historyType, pageType, t]);
  return (
    <div
      className={classNames('syn-history-table', {
        lean: messageClosed,
        'syn-show-trade-history-tabs': showTradeHistoryTabs,
      })}
      ref={containerRef}>
      {isShowSelector && (
        <div ref={selectorRef} className="syn-history-table-header">
          <div className="syn-history-table-header-top">
            {showTradeHistoryTabs && <TradeHistoryTabs />}

            {!showTradeHistoryTabs && <HistoryTableRangeSelector />}
            <div className="syn-history-table-header-top-right">
              <CsvExportButton historyType={historyType} historyTimeRange={timeRange} pair={pair} />

              <Button icon={<RefreshIcon />} type="text" onClick={refresh}>
                {t('common.historyC.refresh')}{' '}
              </Button>
              {showTradeHistoryTabs && <HistoryTableRangeSelector />}
            </div>
          </div>
          <HistoryRefreshSimple onMessageClose={setMessageClosed} />
        </div>
      )}
      <SkeletonTable rowCount={rowCount} loading={loading} columns={columnDefs as SkeletonTableColumnsType[]}>
        <Table
          columns={columnDefs}
          pagination={false}
          id="historyTable"
          key={historyType}
          dataSource={localHistories}
          showUnconnected={false}
          sticky={sticky}
          rowKey={(
            record:
              | TradeHistory
              | OrderHistory
              | FundingHistory
              | TransferHistory
              | LiquidityHistory
              | AccountBalanceHistory,
          ) => record.id + historyType}
          virtual={!!localHistories.length}
          scroll={localHistories.length ? { x: 1000, y: scrollY } : undefined}
          {...emptyDescProps}
        />
      </SkeletonTable>
    </div>
  );
};

export default HistoryTable;
